import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Star, ArrowLeft, TrendingUp, MessageSquare, ThumbsUp, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Card from '@/components/ui/Card';

// --- Types ---

interface ReviewStats {
  total_reviews: string;
  pending_count: string;
  approved_count: string;
  overall_avg_rating: string | null;
  verified_count: string;
  avg_helpful_count: string | null;
}

interface RatingDistributionEntry {
  rating: string;
  count: string;
}

interface TopProduct {
  name: string;
  review_count: string;
  avg_rating: string;
}

interface RatingOverTimeEntry {
  month: string;
  avg_rating: string;
  count: string;
}

interface ReviewAnalyticsData {
  stats: ReviewStats;
  rating_distribution: RatingDistributionEntry[];
  top_products: TopProduct[];
  rating_over_time: RatingOverTimeEntry[];
}

// --- Date range options ---

type DateRange = '3' | '6' | '12' | 'all';

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: '3', label: 'Last 3 months' },
  { value: '6', label: 'Last 6 months' },
  { value: '12', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

// --- Hook ---

function useReviewAnalytics(range: DateRange) {
  const params = range !== 'all' ? `?months=${range}` : '';
  return useQuery<ReviewAnalyticsData>({
    queryKey: ['admin-review-analytics', range],
    queryFn: () => api.get<ReviewAnalyticsData>(`/admin/reviews/analytics${params}`),
  });
}

// --- Empty state ---

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Star className="h-12 w-12 text-stone-300 mb-4" />
      <h2 className="text-lg font-semibold text-stone-700 mb-2">No reviews yet</h2>
      <p className="text-stone-500 max-w-sm">
        Once customers start leaving reviews, your analytics will appear here.
      </p>
    </div>
  );
}

// --- Bar tooltip state ---

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  content: string;
}

// --- Main component ---

export default function ReviewAnalytics() {
  const [range, setRange] = useState<DateRange>('12');
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, content: '' });

  const { data, isLoading, isError } = useReviewAnalytics(range);

  if (isLoading) return <LoadingSpinner className="py-16" />;
  if (isError) return <p className="text-center py-16 text-stone-500">Failed to load analytics.</p>;

  const { stats, rating_distribution, top_products, rating_over_time } = data!;

  const totalReviews = parseInt(stats.total_reviews) || 0;

  const statCards = [
    { label: 'Total Reviews', value: totalReviews, icon: MessageSquare },
    { label: 'Pending', value: parseInt(stats.pending_count) || 0, icon: Star },
    {
      label: 'Avg Rating',
      value: stats.overall_avg_rating ? parseFloat(stats.overall_avg_rating).toFixed(1) : '—',
      icon: TrendingUp,
    },
    { label: 'Verified', value: parseInt(stats.verified_count) || 0, icon: ShieldCheck },
    { label: 'Approved', value: parseInt(stats.approved_count) || 0, icon: ThumbsUp },
    {
      label: 'Avg Helpful',
      value: stats.avg_helpful_count ? parseFloat(stats.avg_helpful_count).toFixed(1) : '0',
      icon: ThumbsUp,
    },
  ];

  const trendLabel =
    DATE_RANGE_OPTIONS.find((o) => o.value === range)?.label ?? 'Rating Trend';

  const handleBarMouseMove = (e: React.MouseEvent<HTMLDivElement>, content: string) => {
    const rect = e.currentTarget.closest('.trend-chart-root')?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({
      visible: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 40,
      content,
    });
  };

  const hideTooltip = () => setTooltip((t) => ({ ...t, visible: false }));

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/reviews" className="text-stone-500 hover:text-stone-700">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-display font-bold text-stone-900">Review Analytics</h1>

        <div className="ml-auto">
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as DateRange)}
            className="text-sm border border-stone-200 rounded-md px-3 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300"
          >
            {DATE_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {totalReviews === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {statCards.map((stat) => (
              <Card key={stat.label} className="p-4 text-center">
                <stat.icon className="h-5 w-5 mx-auto text-stone-400 mb-2" aria-hidden="true" />
                <p className="text-2xl font-bold text-stone-900">{stat.value}</p>
                <p className="text-xs text-stone-500 mt-1">{stat.label}</p>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Rating distribution */}
            <Card className="p-6">
              <h2 className="font-semibold text-stone-900 mb-4">Rating Distribution</h2>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((star) => {
                  const dist = rating_distribution.find(
                    (d) => parseInt(d.rating) === star
                  );
                  const count = dist ? parseInt(dist.count) : 0;
                  const total = totalReviews || 1;
                  const pct = (count / total) * 100;
                  return (
                    <div key={star} className="flex items-center gap-3">
                      <span className="w-12 text-sm text-stone-600">{star} star</span>
                      <div className="flex-1 bg-stone-100 rounded-full h-3.5">
                        <div
                          className="bg-amber-400 rounded-full h-3.5 transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-12 text-right text-sm text-stone-500">{count}</span>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Top products */}
            <Card className="p-6">
              <h2 className="font-semibold text-stone-900 mb-4">Top Reviewed Products</h2>
              <div className="space-y-3">
                {top_products.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-stone-700 truncate flex-1">{p.name}</span>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-stone-500">{p.review_count} reviews</span>
                      <span className="font-medium text-stone-900">
                        {parseFloat(p.avg_rating).toFixed(1)}
                      </span>
                    </div>
                  </div>
                ))}
                {top_products.length === 0 && (
                  <p className="text-sm text-stone-400">No reviews yet</p>
                )}
              </div>
            </Card>

            {/* Rating over time */}
            <Card className="p-6 lg:col-span-2">
              <h2 className="font-semibold text-stone-900 mb-4">Rating Trend — {trendLabel}</h2>
              {rating_over_time.length > 0 ? (
                <div className="trend-chart-root relative" onMouseLeave={hideTooltip}>
                  {/* Tooltip */}
                  {tooltip.visible && (
                    <div
                      className="pointer-events-none absolute z-10 bg-stone-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg"
                      style={{ left: tooltip.x, top: tooltip.y }}
                    >
                      {tooltip.content}
                    </div>
                  )}

                  {/* Y-axis guides */}
                  <div className="flex gap-1 h-40 items-end">
                    {rating_over_time.map((m, i) => {
                      const avgRating = parseFloat(m.avg_rating);
                      const count = parseInt(m.count);
                      const heightPct = (avgRating / 5) * 100;
                      const month = new Date(m.month).toLocaleDateString('en-US', {
                        month: 'short',
                        year: '2-digit',
                      });
                      const tooltipContent = `${month}: ${avgRating.toFixed(1)} avg (${count} review${count !== 1 ? 's' : ''})`;

                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                          <span className="text-xs text-stone-500 leading-none">
                            {avgRating.toFixed(1)}
                          </span>
                          <div className="w-full flex-1 flex items-end">
                            <div
                              className="w-full rounded-t bg-emerald-500 hover:bg-emerald-400 transition-colors duration-150 cursor-default"
                              style={{ height: `${heightPct}%` }}
                              onMouseMove={(e) => handleBarMouseMove(e, tooltipContent)}
                            />
                          </div>
                          <span className="text-xs text-stone-400 truncate w-full text-center leading-none">
                            {new Date(m.month).toLocaleDateString('en-US', { month: 'short' })}
                          </span>
                          <span className="text-xs text-stone-300 leading-none">{m.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-stone-400">No data for this period</p>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
